import { h } from 'preact';
import { useState } from 'preact/hooks';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { MetaRouter } from '../../utils/metarouter';

import { buildMediaQuery, breakpoints } from '../../utils/media-queries';

const Subscription = ({
    id,
    title,
    color,
    url,
    preprice,
    price,
    suffix,
    desc,
    images,
    icon,
    buttonText,
    ageRange,
    alternate,
    ...props
}) => {
    const [selectedImage, setSelectedImage] = useState(0);
    return (
        <Container {...props} alt={alternate}>
            <Carousel>
                <MainImage>
                    {ageRange?.length > 0 && <AgeRange>{ageRange}</AgeRange>}
                    {images.map((img, i) => (
                        <Image
                            key={i}
                            selected={i === selectedImage}
                            src={img.large.src}
                            srcSet={img.large.srcset}
                            width={img.large.width}
                            height={img.large.height}
                            alt={img.large.alt}
                        />
                    ))}
                </MainImage>
                <Thumbs>
                    {images.map((img, i) => (
                        <ThumbWrap key={i}>
                            <Thumb
                                selected={i === selectedImage}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setSelectedImage(i);
                                }}
                                role="button"
                                src={img.thumb.src}
                                srcSet={img.thumb.srcset}
                                width={img.thumb.width}
                                height={img.thumb.height}
                                alt={img.thumb.alt}
                            />
                        </ThumbWrap>
                    ))}
                </Thumbs>
            </Carousel>
            <Content>
                {icon?.src?.length > 0 && (
                    <IconWrap color={color}>
                        <Icon
                            src={icon.src}
                            srcSet={icon.srcset}
                            width={icon.width}
                            height={icon.height}
                            alt={icon.alt}
                        />
                    </IconWrap>
                )}
                {title.length > 0 && <Title>{title}</Title>}

                {(preprice.length > 0 || price.length > 0) && (
                    <Pricing>
                        {preprice.length > 0 && <PrePrice>{preprice}</PrePrice>}
                        {price.length > 0 && (
                            <Price>
                                {price}
                                {suffix.length > 0 && <Suffix>{suffix}</Suffix>}
                            </Price>
                        )}
                    </Pricing>
                )}

                {desc.length > 0 && <Desc>{desc}</Desc>}

                <Button
                    href={url}
                    title={buttonText}
                    onClick={(e) => {
                        sendTrackEvent(price, title);
                    }}>
                    {buttonText}
                </Button>
            </Content>
        </Container>
    );
};

Subscription.propTypes = {
    id: PropTypes.number.isRequired,
    title: PropTypes.string,
    url: PropTypes.string,
    preprice: PropTypes.string,
    price: PropTypes.string,
    suffix: PropTypes.string,
    desc: PropTypes.string,
    images: PropTypes.arrayOf(
        PropTypes.shape({
            large: PropTypes.shape({
                src: PropTypes.string,
                srcset: PropTypes.string,
                width: PropTypes.number,
                height: PropTypes.number,
                alt: PropTypes.string,
            }),
            thumb: PropTypes.shape({
                src: PropTypes.string,
                srcset: PropTypes.string,
                width: PropTypes.number,
                height: PropTypes.number,
                alt: PropTypes.string,
            }),
        }),
    ),
    icon: PropTypes.shape({
        src: PropTypes.string,
        srcset: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
        alt: PropTypes.string,
    }),
    buttonText: PropTypes.string,
    ageRange: PropTypes.string,
    alternate: PropTypes.bool,
    color: PropTypes.string,
};

Subscription.defaultProps = {
    title: '',
    url: '',
    preprice: '',
    price: '',
    suffix: '',
    desc: '',
    color: '#999',
    images: [],
    icon: {},
    buttonText: 'View More',
    ageRange: '',
    alternate: false,
};

const Container = styled.article`
    display: flex;
    flex-direction: column;
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 3px 5px 8px 0 rgba(0, 0, 0, 0.15),
        0 -1px 5px 0 rgba(0, 0, 0, 0.1);
    max-width: 480px;

    @media ${buildMediaQuery(breakpoints.lg)} {
        max-width: none;
        width: 100%;
        flex-direction: ${(p) => (p.alt ? 'row-reverse' : 'row')};
    }
`;

const Carousel = styled.div`
    @media ${buildMediaQuery(breakpoints.lg)} {
        width: 50%;
        display: flex;
        flex-direction: column;
    }
`;

const MainImage = styled.div`
    position: relative;
    height: 0;
    padding-top: 62.5%;

    @media ${buildMediaQuery(breakpoints.lg)} {
        height: 100%;
    }
`;

const AgeRange = styled.div`
    position: absolute;
    top: 16px;
    left: 16px;
    border-radius: 20px;
    padding: 6px 15px;
    color: #fff;
    background: #002d62;
    font-weight: 600;
    font-size: 13px;
    line-height: 1;
    text-align: center;
    z-index: 100;
`;

const Image = styled.img`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: ${(p) => (p.selected ? 1 : 0)};
    transition: opacity 300ms;
`;

const Thumbs = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 8px;
    margin-top: 8px;
    cursor: pointer;
`;

const ThumbWrap = styled.div`
    position: relative;
    width: 100%;
    height: 0;
    padding-top: 62.95%;
`;

const Thumb = styled.img`
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: ${(p) => (p.selected ? 0.75 : 1)};
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 16px 15px 15px;
    box-sizing: border-box;

    @media ${buildMediaQuery(breakpoints.lg)} {
        width: 50%;
        padding: 112px 24px 24px;
        align-items: flex-start;
    }

    @media ${buildMediaQuery(breakpoints.xlg)} {
        padding: 24px;
    }
`;

const Title = styled.h3`
    margin: 0 0 12px;
    padding-right: 0;

    @media ${buildMediaQuery(false, breakpoints.lg)} {
        font-size: 24px;
        line-height: 32px;
        padding-right: 72px;
    }
`;

const IconWrap = styled.div`
    position: absolute;
    top: 0;
    right: 16px;
    width: 64px;
    height: 64px;
    border-radius: 0 0 16px 16px;
    background-color: ${(p) => p.color};

    @media ${buildMediaQuery(breakpoints.lg)} {
        right: auto;
        left: 24px;
        width: 88px;
        height: 88px;
    }
`;

const Icon = styled.img`
    display: block;
    width: 100%;
    height: auto;
`;

const Pricing = styled.div`
    margin: 0 0 12px;
`;

const PrePrice = styled.p`
    margin: 0;
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    color: #242424;

    @media ${buildMediaQuery(breakpoints.lg)} {
        font-size: 14px;
        line-height: 20px;
    }
`;

const Price = styled.span`
    font-family: Kookie;
    font-weight: 700;
    font-size: 20px;
    line-height: 28px;
    color: #242424;

    @media ${buildMediaQuery(breakpoints.lg)} {
        font-size: 24px;
        line-height: 32px;
    }
`;
const Suffix = styled.span`
    font-weight: 600;
    font-size: 12px;
    font-family: Kookie;
    line-height: 28px;
    color: #242424;

    @media ${buildMediaQuery(breakpoints.lg)} {
        font-size: 14px;
        line-height: 32px;
    }
`;

const Desc = styled.p`
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: #242424;
    margin: 0 0 24px;

    @media ${buildMediaQuery(breakpoints.lg)} {
        font-size: 18px;
        line-height: 28px;
    }
`;

const Button = styled.a.attrs({
    className: 'button',
})`
    min-width: 200px;
`;

const sendTrackEvent = (price, title) => {
    if (price.includes('$')) {
        price = price.substring(1, price.length);
    }
    MetaRouter.sendTrackEvent('choose_plan_tapped', {
        screen_name: 'home_page',
        price: price,
        plan: title,
    });
};

export default Subscription;
